import { EventEmitter } from "events";
import { DesktopAgentClient } from "./DesktopAgentClient";
import { DesktopAgentConnectionState } from "./DesktopAgentTypes";
import { desktopBridge, HealthPayload } from "./DesktopBridge";
import { getElectronAgentPort } from "../../utils/platform/electron";
import { isPlatform } from "../../utils/get-platform";

/** Default WebSocket port for Desktop Agent */
const DEFAULT_AGENT_PORT = 8765;

/**
 * Build the WebSocket URL for the Desktop Agent.
 * Uses port from Electron if available, otherwise uses default.
 */
function buildAgentUrl(configUrl?: string): string {
  // If explicit URL provided, use it
  if (configUrl) return configUrl;

  // Try to get port from Electron
  const electronPort = getElectronAgentPort();
  const port = electronPort ?? DEFAULT_AGENT_PORT;

  return `ws://localhost:${port}`;
}

/**
 * Configuration options for DesktopAgentManager
 */
export interface DesktopAgentManagerConfig {
  /** WebSocket URL for Desktop Agent (default: ws://localhost:8765 or Electron-provided port) */
  url?: string;
  /** Health check interval in ms (default: 30000, 0 to disable) */
  healthCheckInterval?: number;
}

/**
 * DesktopAgentManager - Lifecycle manager for DesktopAgentClient
 *
 * Responsibilities:
 * - Explicit initialization (no auto-init on import)
 * - Connection management (connect/disconnect)
 * - Instance lifecycle (create/destroy)
 * - Health checks
 *
 * Usage:
 * 1. Wait for desktopBridge "ready" event
 * 2. Call desktopAgentManager.initialize()
 * 3. Call desktopAgentManager.connect()
 * 4. On cleanup, call desktopAgentManager.destroy()
 */
class DesktopAgentManagerService extends EventEmitter {
  private client: DesktopAgentClient | null = null;
  private healthCheckTimer: NodeJS.Timeout | null = null;
  private config: Required<DesktopAgentManagerConfig> = {
    url: `ws://localhost:${DEFAULT_AGENT_PORT}`,
    healthCheckInterval: 30000,
  };
  private isInitialized = false;

  /**
   * Initialize the manager and create the client instance
   * Does NOT connect - call connect() separately
   * Safe to call multiple times - returns existing client if already initialized
   * Only initializes when running inside Electron desktop app
   *
   * @param config - Optional configuration
   * @returns The created DesktopAgentClient instance
   * @throws Error if called during SSR or outside of Electron
   */
  initialize(config?: DesktopAgentManagerConfig): DesktopAgentClient {
    // Return existing client if already initialized
    if (this.isInitialized && this.client) {
      // eslint-disable-next-line no-console
      console.log(
        "[DesktopAgentManager] Already initialized, returning existing client"
      );
      return this.client;
    }

    // SSR guard
    if (typeof window === "undefined") {
      throw new Error("DesktopAgentManager cannot be initialized during SSR");
    }

    // Platform guard: Only initialize when running on desktop
    // This is a defense-in-depth measure - the provider should already guard this
    if (!isPlatform("desktop")) {
      throw new Error(
        "DesktopAgentManager can only be initialized in the desktop app"
      );
    }

    // eslint-disable-next-line no-console
    console.log("[DesktopAgentManager] Initializing...");

    // Build the agent URL (uses Electron port if available)
    const agentUrl = buildAgentUrl(config?.url);

    // Apply config with resolved URL
    this.config = { ...this.config, ...config, url: agentUrl };

    // eslint-disable-next-line no-console
    console.log(`[DesktopAgentManager] Using agent URL: ${agentUrl}`);

    // Create client instance (autoConnect = false)
    this.client = new DesktopAgentClient(this.config.url, false);
    this.isInitialized = true;

    // Forward client events to manager subscribers
    this.client.on("connected", this.handleConnected);
    this.client.on("disconnected", this.handleDisconnected);
    this.client.on("error", this.handleError);
    this.client.on("reconnecting", this.handleReconnecting);

    // Listen for desktop bridge disconnect request
    desktopBridge.subscribe("desktop:disconnecting", () => {
      this.disconnect();
    });

    // eslint-disable-next-line no-console
    console.log("[DesktopAgentManager] Initialized successfully");

    return this.client;
  }

  /**
   * Connect to the Desktop Agent WebSocket
   * Safe to call multiple times - handles all connection states:
   * - Already connected: returns immediately (no-op)
   * - Currently connecting: returns the pending connection promise
   * - Disconnected: initiates new connection
   *
   * @param force - If true, forces reconnection even if state says connected
   * @throws Error if not initialized
   */
  async connect(force = false): Promise<void> {
    this.ensureInitialized();

    // Use isConnected() which verifies actual WebSocket state, not just the state variable
    // This fixes the bug where state says CONNECTED but WebSocket is actually dead
    const actuallyConnected = this.isConnected();
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const stateConnectionState = this.client!.getConnectionState();

    // eslint-disable-next-line no-console
    console.log("[DesktopAgentManager] Connection status check", {
      stateConnectionState,
      actuallyConnected,
      force,
    });

    // Already connected (verified) - no-op unless force is true
    if (!force && actuallyConnected) {
      // eslint-disable-next-line no-console
      console.log(
        "[DesktopAgentManager] Already connected (verified via WebSocket state), skipping"
      );
      return;
    }

    // Log state mismatch for debugging
    if (
      stateConnectionState === DesktopAgentConnectionState.CONNECTED &&
      !actuallyConnected
    ) {
      // eslint-disable-next-line no-console
      console.log(
        "[DesktopAgentManager] ⚠️ State mismatch: state=CONNECTED but WebSocket not actually connected"
      );
    }

    // eslint-disable-next-line no-console
    console.log(
      `[DesktopAgentManager] Connecting... (current state: ${stateConnectionState}, force: ${force})`
    );
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    await this.client!.connect(force);
  }

  /**
   * Force reconnection to the Desktop Agent
   * Use this when the user explicitly requests a retry/reconnect.
   * This bypasses all state checks and forces a fresh connection.
   *
   * @throws Error if not initialized
   */
  async forceReconnect(): Promise<void> {
    // eslint-disable-next-line no-console
    console.log("[DesktopAgentManager] Force reconnect requested");
    return this.connect(true);
  }

  /**
   * Ensure connected to the Desktop Agent
   * This is the primary method for components to request a connection.
   * Handles all scenarios:
   * - Not initialized: initializes and connects
   * - Already connected: returns immediately (no-op)
   * - Currently connecting: waits for the connection
   * - Disconnected: initiates new connection
   *
   * Safe to call multiple times from different components.
   * Only connects when running inside Electron desktop app.
   */
  async ensureConnected(): Promise<void> {
    // SSR guard
    if (typeof window === "undefined") {
      return;
    }

    // Platform guard: Skip connection attempts outside of desktop
    // This is a defense-in-depth measure - the provider should already guard this
    if (!isPlatform("desktop")) {
      return;
    }

    // Initialize if needed
    if (!this.isInitialized) {
      this.initialize();
    }

    // Connect (handles redundant calls internally)
    await this.connect();
  }

  /**
   * Disconnect from the Desktop Agent
   */
  disconnect(): void {
    this.stopHealthCheck();
    this.client?.disconnect();
  }

  /**
   * Full cleanup - destroy client and manager state
   * Call this when unmounting the provider
   */
  destroy(): void {
    this.stopHealthCheck();

    if (this.client) {
      // Remove listeners first to prevent events during cleanup
      this.client.off("connected", this.handleConnected);
      this.client.off("disconnected", this.handleDisconnected);
      this.client.off("error", this.handleError);
      this.client.off("reconnecting", this.handleReconnecting);

      this.client.disconnect();
      this.client = null;
    }

    this.removeAllListeners();
    this.isInitialized = false;
  }

  /**
   * Check if manager is initialized
   */
  get initialized(): boolean {
    return this.isInitialized;
  }

  /**
   * Get the client instance
   *
   * @throws Error if not initialized
   */
  getClient(): DesktopAgentClient {
    this.ensureInitialized();
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return this.client!;
  }

  /**
   * Get current connection state
   * Returns DISCONNECTED if not initialized
   */
  getConnectionState(): DesktopAgentConnectionState {
    return (
      this.client?.getConnectionState() ??
      DesktopAgentConnectionState.DISCONNECTED
    );
  }

  /**
   * Check if connected to Desktop Agent
   */
  isConnected(): boolean {
    return this.client?.isConnected() ?? false;
  }

  /**
   * Perform health check via bridge query
   * Returns false if not connected or health check fails
   */
  async healthCheck(): Promise<boolean> {
    if (!this.isConnected()) return false;

    try {
      const response = await desktopBridge.query<HealthPayload>(
        "query:agent-health",
        {},
        5000
      );
      const healthy = response.payload?.healthy ?? false;

      if (!healthy) {
        this.emit("health-check-failed", { healthy: false });
      }

      return healthy;
    } catch (error) {
      this.emit("health-check-failed", { error });
      return false;
    }
  }

  /**
   * Send a message via the client
   * Convenience method that delegates to client.send()
   *
   * @throws Error if not connected
   */
  async send<T = unknown>(action: string, payload?: unknown): Promise<T> {
    this.ensureInitialized();
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return this.client!.send<T>(action, payload);
  }

  // ===== Private Methods =====

  /**
   * Ensure manager is initialized before operations
   */
  private ensureInitialized(): void {
    if (!this.isInitialized || !this.client) {
      throw new Error(
        "DesktopAgentManager not initialized. Call initialize() first."
      );
    }
  }

  /**
   * Start periodic health checks
   */
  private startHealthCheck(): void {
    if (this.config.healthCheckInterval <= 0) return;

    // Clear any existing timer
    this.stopHealthCheck();

    this.healthCheckTimer = setInterval(() => {
      this.healthCheck();
    }, this.config.healthCheckInterval);
  }

  /**
   * Stop periodic health checks
   */
  private stopHealthCheck(): void {
    if (this.healthCheckTimer) {
      clearInterval(this.healthCheckTimer);
      this.healthCheckTimer = null;
    }
  }

  // ===== Event Handlers (bound to preserve 'this' context) =====

  private handleConnected = (): void => {
    this.emit("connected");
    this.startHealthCheck();
  };

  private handleDisconnected = (): void => {
    this.emit("disconnected");
    this.stopHealthCheck();
  };

  private handleError = (error: Error): void => {
    this.emit("error", error);
  };

  private handleReconnecting = (data: { attempt: number }): void => {
    this.emit("reconnecting", data);
  };
}

/**
 * Singleton instance of DesktopAgentManagerService
 * Note: Instance is created but NOT initialized on import
 * Call desktopAgentManager.initialize() explicitly after desktop is ready
 */
export const desktopAgentManager = new DesktopAgentManagerService();
